import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Checkbox, Dialog, FormControlLabel } from '@mui/material';
import * as enabledApi from '@shared/api/enabledPlayApi';
import Button from '@shared/components/core/button/CoreButton';
import { BodyMedium } from '@shared/components/core/text/Typography';
import TosConfigModel from '@shared/models/tos/TosConfigModel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DialogChildContentWrapper,
    DialogTitleWrap,
    DisplayDialogTitle,
    StyledDialogActions,
    StyledDialogContent,
} from '../core/dialog/CoreDialogStyles';
import UserMenuButton from '../user/UserMenuButton';

interface ITosAcceptanceDialogProps {
    open: boolean;
    tosConfig: TosConfigModel;
    onAccepted: () => void;
}

export default function TosAcceptanceDialog({ open, tosConfig, onAccepted }: Readonly<ITosAcceptanceDialogProps>) {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

    const handleAccept = async () => {
        setIsSubmitting(true);
        setHasError(false);
        try {
            const result = await enabledApi.acceptTos({
                version: tosConfig.requiredVersion,
                // IP address is intentionally left empty; the server infers it from the request
                ipAddress: '',
            });
            if (result?.resultType === 'Ok') {
                onAccepted();
            } else {
                setHasError(true);
            }
        } catch (e) {
            console.error('Failed to accept TOS', e);
            setHasError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const formattedDate = tosConfig.effectiveDate ? new Date(tosConfig.effectiveDate).toLocaleDateString() : '';

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason !== 'escapeKeyDown') handleAccept();
            }}
            slotProps={{
                paper: {
                    sx: {
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderColor: theme.color.border,
                        background: theme.color.background,
                    },
                },
            }}>
            <DialogTitleWrap>
                <TitleRow>
                    <DisplayDialogTitle>{t('common:tos.title')}</DisplayDialogTitle>
                    <UserMenuButton settingsHref="#" notificationHref="#" skipConfirmation={false} />
                </TitleRow>
            </DialogTitleWrap>
            <StyledDialogContent>
                <DialogChildContentWrapper>
                    <BodyMedium>{t('common:tos.description')}</BodyMedium>
                    {formattedDate && <BodyMedium>{t('common:tos.effectiveDate', { date: formattedDate })}</BodyMedium>}
                    <Button
                        $color="text"
                        $size="small"
                        href="https://cephable.com/terms-of-use/"
                        target="_blank"
                        rel="noopener noreferrer">
                        {t('common:tos.viewInBrowser')}
                    </Button>

                    {hasError && <BodyMedium>{t('common:tos.loadError')}</BodyMedium>}
                </DialogChildContentWrapper>
            </StyledDialogContent>
            <StyledDialogActions>
                <FormControlLabel
                    control={
                        <Checkbox checked={hasAcceptedTerms} onChange={(e) => setHasAcceptedTerms(e.target.checked)} />
                    }
                    label={t('common:tos.acceptCheckbox')}
                />
                <Button
                    $size="small"
                    $color="secondary"
                    onClick={handleAccept}
                    disabled={isSubmitting || !hasAcceptedTerms}>
                    {t('common:tos.accept')}
                </Button>
            </StyledDialogActions>
        </Dialog>
    );
}

const TitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 1rem;
`;
